import { supabase } from '@/lib/supabase'
import type { TablesInsert } from '@planmax/shared-types'
import type {
  ServiceCatalogRow,
  ServiceCatalogOverrideRow,
  ServiceCatalogGroupRow,
  PricingCategoryRow,
  PricingPolicyRow,
  PolicyVersionRow,
  PricingRateRow,
  LocationPricingAssignmentRow,
  LocationCatalogItemRow,
  PricingResolutionResult,
} from '@/types/pricing'

// --------------------------------------------------------------------------
// Service Catalog
// --------------------------------------------------------------------------

export async function fetchSystemCatalog(): Promise<ServiceCatalogRow[]> {
  const { data, error } = await supabase
    .from('service_catalog')
    .select('*')
    .is('organization_id', null)
    .is('deleted_at', null)
    .eq('is_system_item', true)
    .order('sort_order')

  if (error) throw error
  return (data as unknown as ServiceCatalogRow[]) ?? []
}

export async function fetchOrgCatalogItems(): Promise<ServiceCatalogRow[]> {
  const { data, error } = await supabase
    .from('service_catalog')
    .select('*')
    .is('deleted_at', null)
    .order('sort_order')
    .order('name')

  if (error) throw error
  return (data as unknown as ServiceCatalogRow[]) ?? []
}

export async function fetchLocationCatalog(locationId: string) {
  const { data, error } = await supabase.rpc('resolve_location_catalog', {
    p_location_id: locationId,
  })

  if (error) throw error
  return data ?? []
}

export async function createOrgCatalogItem(
  insert: TablesInsert<'service_catalog'>
): Promise<ServiceCatalogRow> {
  const { data, error } = await supabase
    .from('service_catalog')
    .insert(insert)
    .select()
    .single()

  if (error) throw error
  return data as unknown as ServiceCatalogRow
}

export async function updateOrgCatalogItem(
  id: string,
  update: Record<string, unknown>
): Promise<ServiceCatalogRow> {
  const { data, error } = await supabase
    .from('service_catalog')
    .update(update)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data as unknown as ServiceCatalogRow
}

export async function deleteOrgCatalogItem(id: string): Promise<void> {
  const { error } = await supabase
    .from('service_catalog')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

// --------------------------------------------------------------------------
// Catalog Overrides
// --------------------------------------------------------------------------

export async function fetchCatalogOverrides(): Promise<ServiceCatalogOverrideRow[]> {
  const { data, error } = await supabase
    .from('service_catalog_overrides')
    .select('*')

  if (error) throw error
  return (data as unknown as ServiceCatalogOverrideRow[]) ?? []
}

export async function upsertCatalogOverride(
  insert: TablesInsert<'service_catalog_overrides'>
): Promise<ServiceCatalogOverrideRow> {
  const { data, error } = await supabase
    .from('service_catalog_overrides')
    .upsert(insert, { onConflict: 'organization_id,catalog_item_id' })
    .select()
    .single()

  if (error) throw error
  return data as unknown as ServiceCatalogOverrideRow
}

// --------------------------------------------------------------------------
// Catalog Groups
// --------------------------------------------------------------------------

export async function fetchCatalogGroups(): Promise<ServiceCatalogGroupRow[]> {
  const { data, error } = await supabase
    .from('service_catalog_groups')
    .select('*')
    .is('deleted_at', null)
    .eq('is_active', true)
    .order('sort_order')

  if (error) throw error
  return (data as unknown as ServiceCatalogGroupRow[]) ?? []
}

export async function createCatalogGroup(
  insert: TablesInsert<'service_catalog_groups'>
): Promise<ServiceCatalogGroupRow> {
  const { data, error } = await supabase
    .from('service_catalog_groups')
    .insert(insert)
    .select()
    .single()

  if (error) throw error
  return data as unknown as ServiceCatalogGroupRow
}

export async function updateCatalogGroup(
  id: string,
  update: Record<string, unknown>
): Promise<ServiceCatalogGroupRow> {
  const { data, error } = await supabase
    .from('service_catalog_groups')
    .update(update)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data as unknown as ServiceCatalogGroupRow
}

export async function deleteCatalogGroup(id: string): Promise<void> {
  const { error } = await supabase
    .from('service_catalog_groups')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

// --------------------------------------------------------------------------
// Pricing Categories
// --------------------------------------------------------------------------

export async function fetchPricingCategories(): Promise<PricingCategoryRow[]> {
  const { data, error } = await supabase
    .from('pricing_categories')
    .select('*')
    .is('deleted_at', null)
    .order('sort_order')

  if (error) throw error
  return (data as unknown as PricingCategoryRow[]) ?? []
}

export async function createPricingCategory(
  insert: TablesInsert<'pricing_categories'>
): Promise<PricingCategoryRow> {
  const { data, error } = await supabase
    .from('pricing_categories')
    .insert(insert)
    .select()
    .single()

  if (error) throw error
  return data as unknown as PricingCategoryRow
}

export async function updatePricingCategory(
  id: string,
  update: Record<string, unknown>
): Promise<PricingCategoryRow> {
  const { data, error } = await supabase
    .from('pricing_categories')
    .update(update)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data as unknown as PricingCategoryRow
}

export async function deletePricingCategory(id: string): Promise<void> {
  const { error } = await supabase
    .from('pricing_categories')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)

  if (error) throw error
}

// --------------------------------------------------------------------------
// Pricing Policies
// --------------------------------------------------------------------------

export async function fetchPricingPolicies(): Promise<PricingPolicyRow[]> {
  const { data, error } = await supabase
    .from('pricing_policies')
    .select('*')
    .is('deleted_at', null)
    .order('name')

  if (error) throw error
  return (data as unknown as PricingPolicyRow[]) ?? []
}

export async function fetchPricingPolicy(id: string): Promise<PricingPolicyRow> {
  const { data, error } = await supabase
    .from('pricing_policies')
    .select('*')
    .eq('id', id)
    .single()

  if (error) throw error
  return data as unknown as PricingPolicyRow
}

export async function createPricingPolicy(
  insert: TablesInsert<'pricing_policies'>
): Promise<PricingPolicyRow> {
  const { data, error } = await supabase
    .from('pricing_policies')
    .insert(insert)
    .select()
    .single()

  if (error) throw error
  return data as unknown as PricingPolicyRow
}

export async function updatePricingPolicy(
  id: string,
  update: Record<string, unknown>
): Promise<PricingPolicyRow> {
  const { data, error } = await supabase
    .from('pricing_policies')
    .update(update)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data as unknown as PricingPolicyRow
}

export async function archivePricingPolicy(id: string): Promise<void> {
  const { error } = await supabase
    .from('pricing_policies')
    .update({ status: 'archived' })
    .eq('id', id)

  if (error) throw error
}

// --------------------------------------------------------------------------
// Policy Versions
// --------------------------------------------------------------------------

export async function fetchPolicyVersions(policyId: string): Promise<PolicyVersionRow[]> {
  const { data, error } = await supabase
    .from('pricing_policy_versions')
    .select('*')
    .eq('policy_id', policyId)
    .order('version_number', { ascending: false })

  if (error) throw error
  return (data as unknown as PolicyVersionRow[]) ?? []
}

export async function createPolicyVersion(
  insert: TablesInsert<'pricing_policy_versions'>
): Promise<PolicyVersionRow> {
  const { data, error } = await supabase
    .from('pricing_policy_versions')
    .insert(insert)
    .select()
    .single()

  if (error) throw error
  return data as unknown as PolicyVersionRow
}

export async function activatePolicyVersion(versionId: string, policyId: string): Promise<void> {
  // Supersede current active versions
  const { error: supersedeError } = await supabase
    .from('pricing_policy_versions')
    .update({ status: 'superseded' })
    .eq('policy_id', policyId)
    .eq('status', 'active')

  if (supersedeError) throw supersedeError

  // Activate the new version
  const { error: activateError } = await supabase
    .from('pricing_policy_versions')
    .update({ status: 'active' })
    .eq('id', versionId)

  if (activateError) throw activateError
}

// --------------------------------------------------------------------------
// Pricing Rates
// --------------------------------------------------------------------------

export async function fetchPricingRates(versionId: string): Promise<PricingRateRow[]> {
  const { data, error } = await supabase
    .from('pricing_rates')
    .select('*')
    .eq('version_id', versionId)
    .order('sort_order')

  if (error) throw error
  return (data as unknown as PricingRateRow[]) ?? []
}

export async function upsertPricingRate(
  insert: TablesInsert<'pricing_rates'>
): Promise<PricingRateRow> {
  const { data, error } = await supabase
    .from('pricing_rates')
    .upsert(insert)
    .select()
    .single()

  if (error) throw error
  return data as unknown as PricingRateRow
}

export async function deletePricingRate(id: string): Promise<void> {
  const { error } = await supabase
    .from('pricing_rates')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// --------------------------------------------------------------------------
// Location Pricing Assignments
// --------------------------------------------------------------------------

export async function fetchLocationAssignments(
  locationId: string
): Promise<LocationPricingAssignmentRow[]> {
  const { data, error } = await supabase
    .from('location_pricing_assignments')
    .select('*, pricing_policies!policy_id(name, scope, status)')
    .eq('location_id', locationId)
    .order('priority', { ascending: false })

  if (error) throw error
  return (data as unknown as LocationPricingAssignmentRow[]) ?? []
}

export async function createLocationAssignment(
  insert: TablesInsert<'location_pricing_assignments'>
): Promise<LocationPricingAssignmentRow> {
  const { data, error } = await supabase
    .from('location_pricing_assignments')
    .insert(insert)
    .select()
    .single()

  if (error) throw error
  return data as unknown as LocationPricingAssignmentRow
}

export async function updateLocationAssignment(
  id: string,
  update: Record<string, unknown>
): Promise<LocationPricingAssignmentRow> {
  const { data, error } = await supabase
    .from('location_pricing_assignments')
    .update(update)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data as unknown as LocationPricingAssignmentRow
}

export async function deleteLocationAssignment(id: string): Promise<void> {
  const { error } = await supabase
    .from('location_pricing_assignments')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// --------------------------------------------------------------------------
// Location Catalog Items
// --------------------------------------------------------------------------

export async function fetchLocationCatalogItems(
  locationId: string
): Promise<LocationCatalogItemRow[]> {
  const { data, error } = await supabase
    .from('location_catalog_items')
    .select('*')
    .eq('location_id', locationId)

  if (error) throw error
  return (data as unknown as LocationCatalogItemRow[]) ?? []
}

export async function upsertLocationCatalogItem(insert: {
  location_id: string
  catalog_item_id: string
  is_available: boolean
  notes?: string | null
}): Promise<LocationCatalogItemRow> {
  const { data, error } = await supabase
    .from('location_catalog_items')
    .upsert(insert, { onConflict: 'location_id,catalog_item_id' })
    .select()
    .single()

  if (error) throw error
  return data as unknown as LocationCatalogItemRow
}

export async function deleteLocationCatalogItem(id: string): Promise<void> {
  const { error } = await supabase
    .from('location_catalog_items')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// --------------------------------------------------------------------------
// Pricing Resolution
// --------------------------------------------------------------------------

export async function resolvePricingRate(params: {
  locationId: string
  catalogItemCode: string
  aircraftId?: string
  clientId?: string
  serviceDate?: string
}): Promise<PricingResolutionResult> {
  const { data, error } = await supabase.rpc('resolve_pricing_rate', {
    p_location_id: params.locationId,
    p_catalog_item_code: params.catalogItemCode,
    p_aircraft_id: params.aircraftId ?? null,
    p_client_id: params.clientId ?? null,
    p_service_date: params.serviceDate ?? new Date().toISOString().split('T')[0],
  })

  if (error) throw error
  return data as unknown as PricingResolutionResult
}
